// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { apiService } from "../../../services/api/apiService";
import { CAPABILITY_VALUES, isCapability } from "../models/capabilities";
import type { Capability } from "../models/capabilities";
import { shouldUseDemoData } from "../../../services/api/demoData";
import { getRuntimeEnvValue } from "../../../utils/runtimeEnv";
import { TEAMS_FEATURE_ENV_KEY } from "../teamsFeatureEnv";

interface ApiEnvelope<T> {
  data?: T;
  error?: { message: string };
}

interface MeCapabilitiesEnvelope {
  tenantId?: string;
  role?: string;
  mode: string;
  capabilities: string[];
  features?: { teams?: unknown };
}

export interface MeCapabilitiesApiContext {
  token?: string;
  allowTestModeRequests?: boolean;
}

export interface MeCapabilitiesModel {
  tenantId?: string | undefined;
  role?: string | undefined;
  mode: string;
  capabilities: Capability[];
  features: MeFeaturesModel;
}

export interface MeFeaturesModel {
  teams: boolean;
}

function isDemoTeamsFeatureEnabled(): boolean {
  return getRuntimeEnvValue(TEAMS_FEATURE_ENV_KEY) === "true";
}

function unwrapApiEnvelope<T>(envelope: ApiEnvelope<T>): T {
  if (envelope.error !== undefined) {
    throw new Error(envelope.error.message);
  }

  if (envelope.data === undefined) {
    throw new Error("API response was missing the expected data payload.");
  }

  return envelope.data;
}

export async function getMyCapabilities(
  context: MeCapabilitiesApiContext,
): Promise<MeCapabilitiesModel> {
  if (shouldUseDemoData(context)) {
    return {
      tenantId: "tenant-current",
      role: "founding-owner",
      mode: "Enforce",
      capabilities: [...CAPABILITY_VALUES],
      features: { teams: isDemoTeamsFeatureEnabled() },
    };
  }

  const response = await apiService.requestJson<
    ApiEnvelope<MeCapabilitiesEnvelope>
  >({
    path: "/api/me/capabilities",
    method: "GET",
    ...(context.token !== undefined ? { token: context.token } : {}),
  });

  return response
    .map(unwrapApiEnvelope)
    .map((envelope) => ({
      tenantId: envelope.tenantId,
      role: envelope.role,
      mode: envelope.mode,
      capabilities: (envelope.capabilities ?? []).filter(isCapability),
      features: { teams: envelope.features?.teams === true },
    }))
    .unwrap();
}
